import { useState, useEffect } from 'react';
import { api, brl, when, type AccessLogRow, TIER_HINT, TIER_IDS, TIER_NAME, WORKER_APP_URL, type TierId } from '../api';
import { useMeRequired } from '../me';
import { toast } from '../components/toast';
import { PrefSwitch } from '../components/PrefSwitch';
import { isSpecificScope, useScope } from '../scope';
import { LABELS } from '../labels';
import { Link } from 'react-router';
import { Eye, EyeOff, KeyRound, ExternalLink } from 'lucide-react';

const DEV_NEEDS = 'Disponível quando a sua conta tem uma pool dedicada ou on-premise associada';

/** Transparência (F15): quem, entre os administradores da empresa, leu as conversas desta conta e quando. */
function WhoRead() {
  const [rows, setRows] = useState<AccessLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => { setError(null); setRows(null); api<AccessLogRow[]>('/api/client/org/access-log').then(setRows).catch((e) => setError((e as Error).message)); };
  useEffect(load, []);
  return (
    <section className="sec" aria-busy={rows === null && !error}>
      <div className="sec-h"><h2>Quem leu minhas conversas</h2></div>
      <p className="mute" style={{ margin: '0 0 10px', fontSize: 13 }}>Esta conta pertence à empresa: o administrador pode ver as conversas. Cada leitura fica registrada aqui (o registro não guarda o texto).</p>
      {error ? <div className="empty"><b>Não foi possível carregar</b><p>{error}</p><button type="button" className="btn" onClick={load}>Tentar de novo</button></div>
        : rows === null ? <div className="skel" style={{ height: 48 }} aria-hidden="true" />
        : rows.length === 0 ? <div className="empty"><b>Nenhuma leitura registrada</b></div>
        : (
          <div className="tbl"><table>
            <thead><tr><th>quando</th><th>administrador</th><th>o que</th></tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id}><td className="mono">{when(r.createdAt)}</td><td>{r.adminName ?? '—'}</td><td>{r.action === 'read' ? `leu a conversa “${r.chatTitle ?? 'sem título'}”` : 'listou suas conversas'}</td></tr>)}</tbody>
          </table></div>
        )}
    </section>
  );
}

export function Settings() {
  const { me, refresh } = useMeRequired();
  const [profile, setProfile] = useState(me.profile ?? 'buyer');
  const [tier, setTier] = useState<TierId>(me.tier ?? 'shared');

  // Modo Turbo e Modo Desenvolvedor: preferências da conta (PATCH /api/client/preferences, nunca pelo /onboarding)
  const { scopes } = useScope();
  const [busy, setBusy] = useState<'turbo' | 'developer' | null>(null);
  const devPossible = (scopes ?? []).some(isSpecificScope);   // há pool dedicada ou on-premise associada à conta
  const devOff = !(me.prefs?.developer === true) && !devPossible;   // desligar sempre pode; ligar exige uma pool dedicada/on-premise
  const setPref = async (key: 'turbo' | 'developer', on: boolean) => {
    setBusy(key);
    try {
      await api('/api/client/preferences', { method: 'PATCH', body: JSON.stringify({ [key]: on }) });
      await refresh();
      toast(`${key === 'turbo' ? LABELS.turbo : LABELS.developer} ${on ? 'ligado' : 'desligado'}`);
    } catch (e) {
      toast((e as Error).message, 'err');
    } finally {
      setBusy(null);
    }
  };

  const [openrouterKey, setOpenrouterKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [hasKeySaved, setHasKeySaved] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('openrouter_api_key');
    if (saved) {
      setOpenrouterKey(saved);
      setHasKeySaved(true);
    }
  }, []);

  const savePreferences = async () => {
    try {
      await api('/api/client/onboarding', { method: 'POST', body: JSON.stringify({ profile, tier }) });
      await refresh();
      toast('Preferências salvas');
    } catch (e) {
      toast((e as Error).message, 'err');
    }
  };

  const saveOpenRouterKey = () => {
    if (!openrouterKey.trim()) {
      localStorage.removeItem('openrouter_api_key');
      setHasKeySaved(false);
      setOpenrouterKey('');
      toast('Chave do OpenRouter removida');
      return;
    }
    localStorage.setItem('openrouter_api_key', openrouterKey.trim());
    setHasKeySaved(true);
    toast('Chave do OpenRouter salva');
  };

  return (
    <>
      <div className="ph">
        <div>
          <span className="eyebrow">Você</span>
          <h1>Configurações</h1>
        </div>
      </div>
      
      <div className="usina-card">
        <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: '24px', alignItems: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--panel-2)', border: '2px solid var(--line)', display: 'flex', justifyContent: 'center', alignItems: 'center', fontFamily: 'var(--display)', fontSize: '24px', color: 'var(--fil)' }}>
            {me.name ? me.name.charAt(0).toUpperCase() : me.email.charAt(0).toUpperCase()}
          </div>
          <div className="kv">
            <b>nome</b><span>{me.name ?? '—'}</span>
            <b>e-mail</b><span>{me.email}</span>
            <b>clientId</b><span className="mono">{me.clientId}</span>
            <b>{me.creditsScope === 'org' ? 'saldo da empresa' : 'saldo'}</b><span className="mono">{brl(me.credits, 2)}</span>
          </div>
        </div>
        {me.org?.role === 'admin' && <Link className="btn" to="/empresa" style={{ display: 'inline-flex', width: 'auto', marginTop: 14 }}>Gerenciar empresa</Link>}
        <p className="mute" style={{ margin: '14px 0 0', fontSize: 13 }}>
          O clientId acompanha a chave de API no cabeçalho <code className="mono">X-Client-Id</code>. Créditos são adicionados pelo operador da rede.
        </p>
      </div>

      {me.org && <WhoRead />}

      <section className="sec">
        <div className="sec-h"><h2>Como você usa a Clusterizze</h2></div>
        <div className="usina-card" style={{ display: 'flex', flexDirection: 'row', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ flex: 1, minWidth: 200 }}><span>Perfil</span>
            <select value={profile} onChange={(e) => setProfile(e.target.value as typeof profile)}>
              <option value="buyer">Comprar processamento (usar a API)</option>
              <option value="seller">Vender processamento (minhas máquinas)</option>
              <option value="both">Os dois</option>
            </select>
          </label>
          <label className="field" style={{ flex: 1, minWidth: 200 }}><span>Tipo de consumo padrão <span className="mute" style={{fontWeight: 400}}>· novas chaves de API (o chat usa o seletor "Processar em", no menu)</span></span>
            <select value={tier} onChange={(e) => setTier(e.target.value as TierId)}>
              {TIER_IDS.map((v) => <option key={v} value={v}>{TIER_NAME[v]} — {TIER_HINT[v]}</option>)}
            </select>
          </label>
          <button className="btn primary" onClick={savePreferences}>Salvar</button>
        </div>
      </section>

      <section className="sec">
        <div className="sec-h"><h2>Escolha de modelos</h2></div>
        <div className="usina-card">
          <PrefSwitch
            id="pref-turbo" title={LABELS.turbo} checked={me.prefs?.turbo === true} busy={busy === 'turbo'} onChange={(on) => void setPref('turbo', on)}
            text={`Escolha faixas de modelos (Small, Medium, High) por família. O ${LABELS.izze} continua sendo o padrão.`}
          />
          <PrefSwitch
            id="pref-developer" title={LABELS.developer} checked={me.prefs?.developer === true} busy={busy === 'developer'} disabled={devOff} onChange={(on) => void setPref('developer', on)}
            text="Escolha um modelo específico entre os instalados na sua pool dedicada ou on-premise: a forma de testar modelos locais no chat."
            note={devOff && scopes !== null ? DEV_NEEDS : undefined}
          />
        </div>
      </section>

      <section className="sec">
        <div className="sec-h"><h2>Máquinas</h2></div>
        <p className="mute" style={{ margin: '0 0 12px' }}>Conecte e gerencie as máquinas que processam para a rede no app de workers (Hub).</p>
        <a className="btn" href={WORKER_APP_URL} style={{ display: 'inline-flex', width: 'auto' }}>Gerenciar minhas máquinas no Hub <ExternalLink /></a>
      </section>

      <section className="sec">
        <div className="sec-h">
          <h2>Integrações</h2>
        </div>
        <div className="usina-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '8px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <KeyRound size={20} color="var(--fil)" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>OpenRouter API</h3>
                {hasKeySaved && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--ok)', fontSize: '13px', fontFamily: 'var(--sans)' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)', boxShadow: '0 0 8px var(--ok)' }}></div>
                    Conectado
                  </div>
                )}
              </div>
              <p className="mute" style={{ margin: 0, fontSize: '13px', maxWidth: '500px', lineHeight: 1.6 }}>
                Configure sua chave do OpenRouter para acessar modelos de IA como GPT-4, Claude, Gemini e outros diretamente pela Clusterizze.
              </p>
            </div>
            <a 
              href="https://openrouter.ai/keys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
            >
              Obter chave <ExternalLink size={14} />
            </a>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label className="field" style={{ flex: 1, maxWidth: '400px' }}>
              <span>Chave de API (sk-or-v1-...)</span>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                <input 
                  type={showKey ? 'text' : 'password'}
                  className="mono"
                  placeholder="sk-or-v1-..."
                  value={openrouterKey}
                  onChange={(e) => setOpenrouterKey(e.target.value)}
                  style={{ paddingRight: '40px' }}
                />
                <button 
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  aria-label={showKey ? "Ocultar chave" : "Mostrar chave"}
                  style={{ position: 'absolute', right: '8px', background: 'transparent', border: 'none', color: 'var(--mute)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <button className="btn primary" onClick={saveOpenRouterKey}>
              Salvar Chave
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
